import { FetchAutocomplete } from '@australiangreens/ag-internal-components';
import { TextField, Typography } from '@mui/material';
import { Box } from '@mui/system';

import { SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN } from '@/constants';
import DrawerAccordion from '@components/DrawerAccordion';
import { useTemplatePanelContent } from './useTemplatePanelContent';

type TemplatePanelHeaderProps = Pick<
  ReturnType<typeof useTemplatePanelContent>,
  | 'templateDescription'
  | 'handleChangeTemplateDescription'
  | 'preFilteredDomains'
  | 'templateOrganisations'
  | 'handleChangeOrganisations'
  | 'previewDisabled'
  | 'saveDisabled'
  | 'handlePreview'
  | 'handleSaveClick'
>;

export default function TemplatePanelHeader({
  templateDescription,
  handleChangeTemplateDescription,
  preFilteredDomains,
  templateOrganisations,
  handleChangeOrganisations,
}: TemplatePanelHeaderProps) {
  return (
    <>
      <Box
        sx={{
          borderTop: "1px solid #0000001f",
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px'
        }}>
        <TextField
          id="template-description"
          label="Template description"
          type="text"
          fullWidth
          variant="filled"
          value={templateDescription}
          onChange={handleChangeTemplateDescription}
          slotProps={{
            htmlInput: {
              maxLength: SEARCH_TEMPLATE_DESCRIPTION_MAX_LEN,
            },
          }}
          multiline
          minRows={2}
        />
        {preFilteredDomains.length > 1 && (
          <FetchAutocomplete
            minLength={0}
            label="Organisations"
            preLoadedOptions={preFilteredDomains}
            loadingText="Looking for organisations..."
            noOptionsText="No organisations found"
            onChange={handleChangeOrganisations}
            value={templateOrganisations}
            data-testid="domainOrgs"
          />
        )}
      </Box>

      <Box sx={{
        borderBottom: "1px solid #0000001f"
      }}>
        <DrawerAccordion headerText="Template building instructions">
          <Typography component="p">Right click in a field to set it as a placeholder.</Typography>
          <Typography component="p">
            Placeholder fields will be added to the Template builder panel below, where you will
            need to add instructions.
          </Typography>
          <Typography component="p">
            The instructions will need to provide the user with the full context of why they are
            being asked to make a selection for each field.
          </Typography>
          <Typography component="p">
            In order to create a template, there must be at least one field set as a placeholder. If
            you want to share a search that has all fields set, use a Saved Search instead.
          </Typography>
        </DrawerAccordion>
      </Box>
    </>
  );
}
